"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.planHistory = planHistory;
exports.legacyDocumentEffect = legacyDocumentEffect;
const money_1 = require("../money");
const document_rules_1 = require("../posting/document-rules");
const TYPE_LABELS = {
    sale: 'فاتورة بيع',
    sale_return: 'مرتجع بيع',
    purchase: 'فاتورة شراء',
    purchase_return: 'مرتجع شراء',
    receipt: 'تحصيل من عميل',
    payment: 'سداد لمورد',
    expense: 'مصروف',
    adjustment: 'تسوية',
};
/** قيد من طرفين بمبلغ بإشارته: السالب يقلب المدين والدائن، والصفر لا سطر له. */
function pair(debit, credit, amount, currency, memo) {
    if (amount.isZero())
        return [];
    const [dr, cr] = amount.isNegative() ? [credit, debit] : [debit, credit];
    const value = amount.abs();
    return [
        (0, document_rules_1.debitLine)(dr.account, value, currency, { memo, counterparty: dr.counterparty ?? null }),
        (0, document_rules_1.creditLine)(cr.account, value, currency, { memo, counterparty: cr.counterparty ?? null }),
    ];
}
function amountText(value) {
    return value.toFixed(money_1.PRESENTATION_SCALE);
}
function planHistory(input) {
    const { currency, accounts } = input;
    const journals = [];
    const issues = [];
    let archived = 0;
    let posted = 0;
    for (const move of input.movements) {
        if (move.date < input.cutoverDate) {
            archived++;
            continue;
        }
        if (!input.periodEnd || move.date > input.periodEnd) {
            issues.push({
                rowId: move.rowId,
                code: 'HISTORY_AFTER_PERIOD',
                message: move.label +
                    ': بتاريخ ' +
                    move.date +
                    ' بعد نهاية سنة الترحيل — يُرحَّل فى دفعة على سنته.',
            });
            continue;
        }
        posted++;
        const amount = money_1.Money.of(move.amount);
        const paid = money_1.Money.of(move.paid);
        const memo = TYPE_LABELS[move.type] + (move.reference ? ' ' + move.reference : '');
        const party = move.party
            ? {
                account: move.party.role === 'customer' ? input.receivableAccount : input.payableAccount,
                counterparty: { type: move.party.role, id: move.party.key },
            }
            : null;
        const needsParty = ['receipt', 'payment', 'adjustment'].includes(move.type);
        if (needsParty && !party) {
            issues.push({
                rowId: move.rowId,
                code: 'HISTORY_PARTY_REQUIRED',
                message: move.label + ': ' + TYPE_LABELS[move.type] + ' بلا طرف.',
            });
            continue;
        }
        const needsAccount = ['receipt', 'payment', 'expense', 'adjustment'].includes(move.type);
        if (needsAccount && !move.counterAccount) {
            issues.push({
                rowId: move.rowId,
                code: 'HISTORY_ACCOUNT_REQUIRED',
                message: move.label + ': لا حساب مقابل لـ' + TYPE_LABELS[move.type] + '.',
            });
            continue;
        }
        // فاتورة الطرف النقدى: كأنها مسددة بالكامل، فالطرف هو الوسيط
        const holder = party ?? { account: accounts.clearing };
        const clearing = { account: accounts.clearing };
        const other = { account: move.counterAccount ?? accounts.clearing };
        const lines = [];
        switch (move.type) {
            case 'sale':
                lines.push(...pair(holder, { account: accounts.revenue }, amount, currency, memo));
                if (party)
                    lines.push(...pair(clearing, party, paid, currency, memo + ' — مدفوع'));
                break;
            case 'sale_return':
                lines.push(...pair({ account: accounts.salesReturns }, holder, amount, currency, memo));
                break;
            case 'purchase':
                lines.push(...pair({ account: accounts.purchases }, holder, amount, currency, memo));
                if (party)
                    lines.push(...pair(party, clearing, paid, currency, memo + ' — مدفوع'));
                break;
            case 'purchase_return':
                lines.push(...pair(holder, { account: accounts.purchaseReturns }, amount, currency, memo));
                break;
            case 'receipt':
                lines.push(...pair(other, party, amount, currency, memo));
                break;
            case 'payment':
                lines.push(...pair(party, other, amount, currency, memo));
                break;
            case 'expense':
                lines.push(...pair(other, clearing, amount, currency, memo));
                break;
            case 'adjustment':
                // موجب = مدين على الطرف
                lines.push(...pair(party, other, amount, currency, memo));
                break;
        }
        if (lines.length === 0)
            continue;
        journals.push({
            rowId: move.rowId,
            postingDate: move.date,
            reference: move.reference,
            memo: memo + (move.memo ? ' — ' + move.memo : ''),
            lines,
        });
    }
    // أثر القيود على كل طرف (موجب مدين) — من السطور نفسها، فما يُطابَق هو ما يُرحَّل
    const effect = new Map();
    for (const journal of journals) {
        for (const line of journal.lines) {
            const key = line.counterparty?.id;
            if (!key)
                continue;
            effect.set(key, (effect.get(key) ?? money_1.Money.zero()).add(line.debit).sub(line.credit));
        }
    }
    return { journals, archived, posted, issues, check: historyCheck(input, effect) };
}
function historyCheck(input, effect) {
    const base = {
        code: 'HISTORY_MATCH',
        expected: null,
        actual: null,
        difference: null,
        suspects: [],
    };
    if (input.movements.length === 0) {
        return { ...base, status: 'na', detail: 'لا حركات تاريخية فى الدفعة.' };
    }
    const compared = input.parties.filter((party) => party.expected !== null);
    if (compared.length === 0) {
        return {
            ...base,
            status: 'na',
            detail: 'لا رصيد حالى للأطراف فى الدفعة — املأ عمود «الرصيد الحالى للمطابقة» فى ملفى العملاء والموردين ليُطابَق كل طرف بعد الحركات.',
        };
    }
    let expected = money_1.Money.zero();
    let actual = money_1.Money.zero();
    const wrong = [];
    for (const party of compared) {
        // بطبيعة الطرف: المورد موجبه دائن، فأثر القيود عليه بعكس إشارته
        const sign = party.role === 'customer' ? 1 : -1;
        const moved = (effect.get(party.key) ?? money_1.Money.zero()).mul(sign);
        const result = money_1.Money.of(party.opening).add(moved);
        const target = money_1.Money.of(party.expected);
        expected = expected.add(target);
        actual = actual.add(result);
        const gap = result.sub(target).normalize();
        if (!gap.isZero())
            wrong.push({ party, gap });
    }
    const difference = actual.sub(expected).normalize();
    if (wrong.length === 0) {
        return {
            ...base,
            status: 'ok',
            expected,
            actual,
            difference,
            detail: 'رصيد كل طرف (' +
                compared.length +
                ') بعد الحركات يساوى رصيده الحالى فى البرنامج السابق بالقرش.',
        };
    }
    const sorted = [...wrong].sort((a, b) => b.gap.abs().cmp(a.gap.abs()));
    return {
        ...base,
        status: 'fail',
        expected,
        actual,
        difference,
        detail: wrong.length +
            ' طرف رصيده بعد الحركات لا يساوى رصيده الحالى — أكبرها «' +
            sorted[0].party.label +
            '» بفرق ' +
            amountText(sorted[0].gap) +
            '. راجع حركات ناقصة أو مكررة أو رصيدًا افتتاحيًا بتاريخ غير تاريخ الترحيل.',
        suspects: sorted.slice(0, 5).map(({ party }) => ({
            kind: party.role === 'customer' ? 'customers' : 'suppliers',
            rowId: party.rowId,
            label: party.label,
        })),
    };
}
/**
 * أثر مستند أرشيف على حساب طرفه، مدين ودائن، كما كان قيده سيكون لو قُيِّد — نفس جدول `planHistory`:
 * البيع مدين بقيمته ودائن بالمدفوع عليه، والمرتجع والتحصيل دائن، والشراء دائن ومدفوعه مدين، والسداد
 * ومرتجع الشراء والتسوية مدين. السالب يقلب الجهة. المصروف بلا طرف، فلا أثر له.
 *
 * كشف الطرف من أول التعامل يعرض به سنين ما قبل الترحيل سطرًا سطرًا برصيد جارٍ.
 */
function legacyDocumentEffect(type, amount, paid) {
    let debit = money_1.Money.zero();
    let credit = money_1.Money.zero();
    const on = (side, value) => {
        if (value.isZero())
            return;
        const flipped = value.isNegative() ? (side === 'debit' ? 'credit' : 'debit') : side;
        if (flipped === 'debit')
            debit = debit.add(value.abs());
        else
            credit = credit.add(value.abs());
    };
    const value = money_1.Money.of(amount);
    const cash = money_1.Money.of(paid);
    switch (type) {
        case 'sale':
            on('debit', value);
            on('credit', cash);
            break;
        case 'purchase':
            on('credit', value);
            on('debit', cash);
            break;
        case 'sale_return':
        case 'receipt':
            on('credit', value);
            break;
        case 'purchase_return':
        case 'payment':
        case 'adjustment':
            on('debit', value);
            break;
        case 'expense':
            break;
    }
    return { debit, credit };
}
//# sourceMappingURL=history.js.map